import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './routes';
import { UpgradeModal } from './components/billing/UpgradeModal';
// Side-effect import: applies persisted theme to <html data-theme> before first paint.
import './stores/themeStore';
// BP 56 — side-effect import: initializes i18next (bundled `en`, so first
// paint is always correct) and negotiates/applies the resolved UI locale.
// Same idiom as themeStore above. See frontend/src/i18n/index.ts.
import './i18n';

// Stale-deploy recovery. After a redeploy the hashed code-split chunks change,
// but a browser (or CDN) holding the OLD index.html still tries to import the
// previous chunk filenames — which now 404, surfacing as React Router's
// "Importing a module script failed" error screen on lazy routes (e.g. /chat).
// Vite fires `vite:preloadError` when a dynamic import fails; we reload to pull
// the fresh index.html + chunks. audit fe-rel-6: the old 10s-only guard re-
// reloaded every 10s FOREVER when a chunk was genuinely gone (bad deploy / CDN
// purge), not merely stale. Cap retries, then stop and show a stable banner.
function showStaleVersionBanner() {
  if (document.getElementById('ws-stale-banner')) return;
  const bar = document.createElement('div');
  bar.id = 'ws-stale-banner';
  bar.setAttribute('role', 'alert');
  bar.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:99999;display:flex;gap:12px;align-items:center;justify-content:center;padding:10px 16px;background:#1f2937;color:#fff;font:500 13px system-ui,sans-serif;box-shadow:0 -2px 12px rgba(0,0,0,.25)';
  const label = document.createElement('span');
  label.textContent = 'A new version of Worksnip is available.';
  bar.appendChild(label);
  const btn = document.createElement('button');
  btn.textContent = 'Refresh';
  btn.style.cssText = 'background:#fff;color:#111;border:0;border-radius:6px;padding:6px 14px;font-weight:600;cursor:pointer';
  btn.onclick = () => { try { sessionStorage.removeItem('ws:chunk-reload'); } catch { /* noop */ } window.location.reload(); };
  bar.appendChild(btn);
  document.body.appendChild(bar);
}

window.addEventListener('vite:preloadError', (e) => {
  const KEY = 'ws:chunk-reload';
  let st: { n: number; at: number } = { n: 0, at: 0 };
  try { st = { n: 0, at: 0, ...JSON.parse(sessionStorage.getItem(KEY) || '{}') }; } catch { /* fresh */ }
  const now = Date.now();
  // A reload >60s ago is a separate incident — reset the counter.
  if (!st.at || now - st.at > 60_000) st = { n: 0, at: 0 };
  if (st.n < 2 && now - (st.at || 0) > 10_000) {
    sessionStorage.setItem(KEY, JSON.stringify({ n: st.n + 1, at: now }));
    window.location.reload();
    return;
  }
  // Retries exhausted within the window → the chunk is genuinely gone. Stop the
  // reload loop and surface a one-click Refresh banner instead.
  if (e && typeof (e as Event).preventDefault === 'function') (e as Event).preventDefault();
  showStaleVersionBanner();
});

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
    {/* Monetization 360 — global 402 upgrade modal, driven by billingStore. */}
    <UpgradeModal />
  </React.StrictMode>
);
